/**
 * EventFoundry Tracker Script
 * Embedded script that fetches event definitions and tracks user interactions
 */

import { initTracking } from './tracker.js';
import { isInIframe, initEditorMode } from './editor-mode.js';
import { devLog } from './dev-logger.js';
import { getAdapter } from './adapters/registry.js';

// Auto-detect API endpoint based on environment
const isLocal = window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

const API_ENDPOINT = isLocal
  ? 'http://localhost/api/tracking/events'
  : 'https://eventfoundry.app/api/tracking/events';

/**
 * Generate site-specific cache key for event data
 * @param {string} siteKey - Site key
 * @returns {string} Cache key
 */
function getCacheKey(siteKey) {
  return `eventfoundry_cache_${siteKey}`;
}

/**
 * Extract configuration from script tag attributes
 * @returns {Object|null} Config object with siteKey and devMode, or null if script tag not found
 */
function getScriptConfig() {
  const script = document.currentScript ||
    document.querySelector('script[data-site-key]');

  if (!script) {
    console.error('EventFoundry: Could not find script tag');
    return null;
  }

  const devModeAttr = script.getAttribute('data-dev-mode');

  // Auto-detect dev mode: localhost OR explicit data-dev-mode="true"
  // Can be overridden with data-dev-mode="false" to force production mode on localhost
  const devMode = devModeAttr === 'false' ? false : (isLocal || devModeAttr === 'true');

  return {
    siteKey: script.getAttribute('data-site-key'),
    devMode
  };
}

/**
 * Validate destination configuration
 * @param {Object} config - Destination config object
 * @returns {boolean} - True if valid
 */
function validateDestinationConfig(config) {
  const { destination, credentials, enabled } = config;

  // Required fields
  if (!destination || typeof destination !== 'string') {
    console.error('EventFoundry: Invalid destination config - missing destination name');
    return false;
  }

  if (typeof enabled !== 'boolean') {
    console.error(`EventFoundry: Invalid ${destination} config - missing enabled field`);
    return false;
  }

  if (!credentials || typeof credentials !== 'object') {
    console.error(`EventFoundry: Invalid ${destination} config - missing credentials`);
    return false;
  }

  // GA4-specific validation
  if (destination === 'GA4') {
    const { measurementId } = credentials;
    if (!measurementId || !measurementId.match(/^G-[A-Z0-9]+$/)) {
      console.error(`EventFoundry: Invalid GA4 measurement ID: ${measurementId}`);
      return false;
    }
  }

  // Meta Pixel-specific validation
  if (destination === 'META_PIXEL') {
    const { pixelId } = credentials;
    if (!pixelId || !pixelId.match(/^\d+$/)) {
      console.error(`EventFoundry: Invalid Meta Pixel ID: ${pixelId}`);
      return false;
    }
  }

  return true;
}

/**
 * Initialize all enabled destinations
 * @param {Array} destinationConfigs - Array of destination configs from API
 * @returns {Promise<Object>} - Object mapping destination names to initialization status
 */
async function initializeDestinations(destinationConfigs) {
  const initResults = {};

  // Filter to enabled and valid destinations only
  const enabledConfigs = destinationConfigs.filter(config => config.enabled);
  const validConfigs = enabledConfigs.filter(config => validateDestinationConfig(config));

  if (enabledConfigs.length === 0) {
    console.warn('EventFoundry: No destinations enabled');
    return initResults;
  }

  if (validConfigs.length === 0) {
    console.error('EventFoundry: No valid destination configurations found');
    return initResults;
  }

  if (validConfigs.length < enabledConfigs.length) {
    console.warn(`EventFoundry: ${enabledConfigs.length - validConfigs.length} destination(s) skipped due to invalid configuration`);
  }

  console.log(`EventFoundry: Initializing ${validConfigs.length} destination(s)`);

  // Initialize each destination
  for (const config of validConfigs) {
    const { destination, credentials } = config;
    const adapter = getAdapter(destination);

    if (!adapter) {
      initResults[destination] = false;
      continue;
    }

    try {
      const success = await adapter.initialize(credentials);
      initResults[destination] = success;

      if (success) {
        console.log(`✓ ${destination} initialized`);
      } else {
        console.error(`✗ ${destination} initialization failed`);
      }
    } catch (error) {
      console.error(`EventFoundry: ${destination} initialization error:`, error);
      initResults[destination] = false;
    }
  }

  return initResults;
}

/**
 * Main initialization function
 * Fetches event definitions and sets up tracking
 */
async function init() {
  // Get script configuration
  const config = getScriptConfig();
  if (!config || !config.siteKey) {
    console.error('EventFoundry: No site key provided');
    return;
  }

  const { siteKey, devMode } = config;

  // Log initialization (1 line)
  console.log(`✓ EventFoundry tracker loaded (v${__VERSION__})`);

  // Check if we're in an iframe (potential editor mode)
  if (isInIframe()) {
    // Initialize editor mode detection, passing reload callback
    initEditorMode(reloadEventDefinitions);
  }

  // Log dev mode status (uses devLog to prevent stripping by build)
  devLog(devMode, '⚠ Dev mode: Events disabled');
  devLog(!devMode, '✓ Production mode: Events enabled');

  try {
    // Fetch event configuration
    const data = await fetchEventDefinitions(siteKey);

    // Initialize all enabled destinations
    const initResults = await initializeDestinations(data.destinationConfigs);

    // Check if at least one destination initialized successfully
    const hasAnyDestination = Object.values(initResults).some(success => success);

    if (!hasAnyDestination && !isInIframe()) {
      console.warn('EventFoundry: No destinations available - tracking disabled');
      return;
    }

    // Extract events array from response
    const events = data.events;

    // Initialize event tracking
    const trackingInitialized = initTracking(events, devMode, initResults);

    // If tracking failed due to invalid selectors and data came from cache,
    // clear cache and try fetching fresh data from API
    if (!trackingInitialized && data._fromCache) {
      console.log('EventFoundry: Tracking failed with cached data, clearing cache and refetching...');
      clearCache(siteKey);

      // Fetch fresh data (bypassing cache)
      const freshData = await fetchEventDefinitions(siteKey);
      const freshInitResults = await initializeDestinations(freshData.destinationConfigs);
      const freshEvents = freshData.events;

      // Try initializing with fresh data
      initTracking(freshEvents, devMode, freshInitResults);
    }
  } catch (error) {
    console.error('EventFoundry: Failed to initialize:', error);
  }
}

/**
 * Fetch event definitions with ETag-based caching
 * @param {string} siteKey - Site key for fetching event definitions
 * @returns {Promise<Object>} Event definitions response object with events, destinationConfigs, and version
 */
async function fetchEventDefinitions(siteKey) {
  // Get cached data and ETag
  const cached = getFromCache(siteKey);
  const cachedETag = getETagFromCache(siteKey);

  // Prepare request headers
  const headers = {};
  if (cachedETag) {
    headers['If-None-Match'] = cachedETag;
  }

  // Fetch from API with conditional request
  const response = await fetch(`${API_ENDPOINT}?siteKey=${siteKey}`, {
    headers: headers,
    cache: 'no-store'
  });

  // Handle 304 Not Modified - use cached data
  if (response.status === 304) {
    if (cached) {
      // Validate cached data has required fields
      if (cached.events && cached.destinationConfigs && typeof cached.version === 'number') {
        return cached;
      }
      // If cached data is invalid, fall through to refetch
      console.warn('EventFoundry: Cached data invalid, refetching...');
    }
    // Fallback: if we don't have cached data but got 304, fetch again without ETag
    const fallbackResponse = await fetch(`${API_ENDPOINT}?siteKey=${siteKey}`, {
      cache: 'no-store'
    });
    if (!fallbackResponse.ok) {
      throw new Error(`API returned ${fallbackResponse.status}`);
    }
    const data = await fallbackResponse.json();

    // Validate fallback response
    if (!data.events || !Array.isArray(data.events)) {
      throw new Error('Invalid response: missing events array');
    }
    if (!data.destinationConfigs || !Array.isArray(data.destinationConfigs)) {
      throw new Error('Invalid response: missing destinationConfigs array');
    }
    if (typeof data.version !== 'number') {
      throw new Error('Invalid response: missing version number');
    }

    const etag = fallbackResponse.headers.get('etag');
    saveToCache(siteKey, data, etag);
    return data;
  }

  if (!response.ok) {
    throw new Error(`API returned ${response.status}`);
  }

  // Handle 200 OK - parse new data and update cache
  const data = await response.json();

  // Validate response structure
  if (!data.events || !Array.isArray(data.events)) {
    throw new Error('Invalid response: missing events array');
  }
  if (!data.destinationConfigs || !Array.isArray(data.destinationConfigs)) {
    throw new Error('Invalid response: missing destinationConfigs array');
  }
  if (typeof data.version !== 'number') {
    throw new Error('Invalid response: missing version number');
  }

  // Warn if no destinations configured
  if (data.destinationConfigs.length === 0) {
    console.warn('EventFoundry: No destinations configured - events will not be sent');
  }

  const etag = response.headers.get('etag');
  saveToCache(siteKey, data, etag);

  return data;
}

/**
 * Get cached event data from localStorage
 * @param {string} siteKey - Site key
 * @returns {Object|null} Cached definitions or null
 */
function getFromCache(siteKey) {
  try {
    const cacheKey = getCacheKey(siteKey);
    const item = localStorage.getItem(cacheKey);
    if (!item) return null;

    const cacheData = JSON.parse(item);

    // Extract the actual data (without cache metadata)
    const { _etag, _cachedAt, ...data } = cacheData;

    // Mark data as coming from cache
    data._fromCache = true;
    return data;
  } catch (error) {
    console.warn('EventFoundry: Cache read failed:', error);
    return null;
  }
}

/**
 * Get cached ETag from localStorage
 * @param {string} siteKey - Site key
 * @returns {string|null} Cached ETag or null
 */
function getETagFromCache(siteKey) {
  try {
    const cacheKey = getCacheKey(siteKey);
    const item = localStorage.getItem(cacheKey);
    if (!item) return null;

    const cacheData = JSON.parse(item);
    return cacheData._etag || null;
  } catch (error) {
    console.warn('EventFoundry: ETag read failed:', error);
    return null;
  }
}

/**
 * Save to localStorage with ETag
 * @param {string} siteKey - Site key
 * @param {Object} data - Event definitions response to cache
 * @param {string|null} etag - ETag header from response
 */
function saveToCache(siteKey, data, etag) {
  try {
    const cacheKey = getCacheKey(siteKey);

    // Normalize ETag by removing W/ prefix if present (Cloudflare adds this)
    const normalizedETag = etag ? etag.replace(/^W\//, '') : null;

    // Combine event data and ETag into single cache object
    const cacheData = {
      ...data,
      _etag: normalizedETag,
      _cachedAt: Date.now()
    };

    // Save combined data
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('EventFoundry: Cache write failed:', error);
  }
}

/**
 * Clear cache for a specific site key
 * @param {string} siteKey - Site key
 */
function clearCache(siteKey) {
  try {
    const cacheKey = getCacheKey(siteKey);
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.warn('EventFoundry: Failed to clear cache:', error);
  }
}

/**
 * Reload event definitions (for editor mode)
 * Clears cache and refetches from API
 */
export async function reloadEventDefinitions() {
  const config = getScriptConfig();
  if (!config || !config.siteKey) {
    console.error('EventFoundry: No site key available for reload');
    return;
  }

  const { siteKey, devMode } = config;

  // Clear cache
  console.log('EventFoundry: Cache cleared, reloading definitions');
  clearCache(siteKey);

  // Fetch fresh definitions
  try {
    const data = await fetchEventDefinitions(siteKey);

    // Re-initialize destinations
    const initResults = await initializeDestinations(data.destinationConfigs);
    const events = data.events;

    // Re-initialize tracking with fresh definitions
    initTracking(events, devMode, initResults);
    console.log('EventFoundry: Definitions reloaded successfully');
  } catch (error) {
    console.error('EventFoundry: Failed to reload definitions', error);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
